import React, { useEffect, useState, useMemo } from 'react';
import styled from 'styled-components';
import { getColonyLogs, createColonyLog } from '../../../../../../services/apiaryService';
import close from '../../../assets/images/close.png';
import * as Styles from '../../../Farmers/components/FarmerForm/style';
import { Row, Col, Button, Form, Modal } from 'react-bootstrap';
import LoadingOverlay from 'react-loading-overlay';
import BounceLoader from 'react-spinners/BounceLoader';
import BootstrapTable from 'react-bootstrap-table-next';
import paginationFactory from 'react-bootstrap-table2-paginator';
import { Select, DatePicker, Empty, Tag, InputNumber, Divider } from 'antd';
import moment from 'moment';
import { toast } from 'react-toastify';
import 'react-bootstrap-table-next/dist/react-bootstrap-table2.min.css';
import 'react-bootstrap-table2-paginator/dist/react-bootstrap-table2-paginator.min.css';

const { Option } = Select;

// --- Styled Components ---

const MainContainer = styled.div`
    padding: 20px 0;
`;

const HeaderSection = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 24px;
`;

const SummaryCards = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: 20px;
    margin-bottom: 30px;
`;

const StatCard = styled.div`
    background: linear-gradient(135deg, ${props => props.color || '#ff8701'} 0%, ${props => props.color2 || '#ffbc6d'} 100%);
    padding: 20px;
    border-radius: 16px;
    color: white;
    box-shadow: 0 8px 16px rgba(0,0,0,0.08);
    transition: transform 0.3s ease;

    &:hover {
        transform: translateY(-5px);
    }

    .label {
        font-size: 13px;
        font-weight: 600;
        opacity: 0.9;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        margin-bottom: 8px;
    }

    .value {
        font-size: 28px;
        font-weight: 800;
        margin: 0;
    }

    .sub-value {
        font-size: 12px;
        margin-top: 5px;
        opacity: 0.8;
    }
`;

const TableWrapper = styled.div`
    background: #fff;
    border-radius: 12px;
    box-shadow: 0 4px 12px rgba(0,0,0,0.03);
    overflow: hidden;
    border: 1px solid #eaeaea;

    .modern-table thead th {
        background: #fafafa;
        color: #636e72;
        font-weight: 700;
        font-size: 12px;
        padding: 16px;
        text-transform: uppercase;
        border-bottom: 2px solid #55efc4 !important;
    }

    .modern-table tbody td {
        padding: 16px;
        vertical-align: middle;
        font-size: 14px;
        border-top: 1px solid #f8f8f8;
    }

    .modern-table tbody tr:hover {
        background-color: #f0fff4 !important;
    }
`;

const TypeBadge = styled.span`
    padding: 4px 12px;
    border-radius: 6px;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    background: ${props => props.type === 'ADD' ? '#e6fffa' : '#fff5f5'};
    color: ${props => props.type === 'ADD' ? '#00b894' : '#d63031'};
    border: 1px solid ${props => props.type === 'ADD' ? '#b2f2bb' : '#ffa8a8'};
`;

// --- Colony Log Form Modal ---

const ColonyLogFormModal = ({ show, onHide, apiaryId, onSuccess }) => {
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        type: 'ADD',
        source_or_reason: 'NEW_PURCHASE',
        date: moment(),
        count: 1
    });

    const handleTypeChange = (type) => {
        const defaultReason = type === 'ADD' ? 'NEW_PURCHASE' : 'WEATHER_MORTALITY';
        setFormData({ ...formData, type, source_or_reason: defaultReason });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const payload = {
                ...formData,
                date: formData.date.toISOString(),
                count: parseInt(formData.count)
            };
            await createColonyLog(apiaryId, payload);
            toast.success('Colony log updated successfully');
            onSuccess();
            onHide();
        } catch (error) {
            toast.error(error.response?.data?.error || 'Failed to update colony logs');
        } finally {
            setLoading(false);
        }
    };

    const addReasons = [
        { value: 'NEW_PURCHASE', label: 'New purchase', color: 'cyan' },
        { value: 'COLONY_MULTIPLICATION', label: 'Colony multiplication', color: 'blue' }
    ];

    const subtractReasons = [
        { value: 'WEATHER_MORTALITY', label: 'Weather mortality', color: 'orange' },
        { value: 'MIGRATION_MORTALITY', label: 'Migration mortality', color: 'volcano' },
        { value: 'PEST_MORTALITY', label: 'Pest mortality', color: 'red' }
    ];

    const reasons = formData.type === 'ADD' ? addReasons : subtractReasons;

    return (
        <Modal show={show} onHide={onHide} centered>
            <Modal.Header closeButton style={{ border: 'none', padding: '24px 24px 10px' }}>
                <Modal.Title style={{ fontWeight: 800, color: '#2d3436' }}>
                    <i className={`fa ${formData.type === 'ADD' ? 'fa-plus-circle' : 'fa-minus-circle'} mr-2`} 
                       style={{ color: formData.type === 'ADD' ? '#00b894' : '#d63031' }}></i>
                    Hive adjustment
                </Modal.Title>
            </Modal.Header>
            <Form onSubmit={handleSubmit}>
                <Modal.Body style={{ padding: '0 24px 24px' }}>
                    <p style={{ fontSize: '14px', color: '#636e72', marginBottom: '24px' }}>
                        Record manual additions or subtractions from this apiary's hive count.
                    </p>

                    <Form.Group className="mb-4">
                        <Form.Label style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', color: '#b2bec3' }}>Transaction Type</Form.Label>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                            <Button 
                                variant={formData.type === 'ADD' ? 'success' : 'outline-secondary'}
                                onClick={() => handleTypeChange('ADD')}
                                style={{ borderRadius: '8px', fontWeight: 600, fontSize: '14px' }}
                            >
                                <i className="fa fa-plus-circle mr-2"></i> Addition
                            </Button>
                            <Button 
                                variant={formData.type === 'SUBTRACT' ? 'danger' : 'outline-secondary'}
                                onClick={() => handleTypeChange('SUBTRACT')}
                                style={{ borderRadius: '8px', fontWeight: 600, fontSize: '14px' }}
                            >
                                <i className="fa fa-minus-circle mr-2"></i> Subtraction
                            </Button>
                        </div>
                    </Form.Group>

                    <Row>
                        <Col md={12}>
                            <Form.Group className="mb-4">
                                <Form.Label style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', color: '#b2bec3' }}>Source / Reason*</Form.Label>
                                <Select
                                    style={{ width: '100%', height: '42px' }}
                                    value={formData.source_or_reason}
                                    onChange={(v) => setFormData({ ...formData, source_or_reason: v })}
                                >
                                    {reasons.map(r => (
                                        <Option key={r.value} value={r.value}>{r.label}</Option>
                                    ))}
                                </Select>
                            </Form.Group>
                        </Col>
                    </Row>

                    <Row>
                        <Col md={6}>
                            <Form.Group className="mb-4">
                                <Form.Label style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', color: '#b2bec3' }}>Date*</Form.Label>
                                <DatePicker 
                                    style={{ width: '100%', height: '42px' }}
                                    value={formData.date}
                                    onChange={d => setFormData({ ...formData, date: d })}
                                    allowClear={false}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group className="mb-4">
                                <Form.Label style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', color: '#b2bec3' }}>Number of Colonies*</Form.Label>
                                <InputNumber
                                    min={1}
                                    style={{ width: '100%', height: '42px', borderRadius: '4px', paddingTop: '4px' }}
                                    value={formData.count}
                                    onChange={v => setFormData({ ...formData, count: v })}
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                </Modal.Body>
                <Modal.Footer style={{ border: 'none', padding: '0 24px 24px' }}>
                    <Button variant="light" onClick={onHide} disabled={loading} style={{ borderRadius: '8px', fontWeight: 600 }}>
                        Cancel
                    </Button>
                    <Button 
                        type="submit" 
                        disabled={loading}
                        style={{ 
                            background: formData.type === 'ADD' ? '#00b894' : '#d63031',
                            borderColor: formData.type === 'ADD' ? '#00b894' : '#d63031',
                            borderRadius: '8px',
                            fontWeight: 700,
                            padding: '8px 24px'
                        }}
                    >
                        {loading ? 'Saving...' : 'Confirm Adjustment'}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
};

// --- Main Component ---

const NectarApiaryStrength = ({ apiaryId, refreshApiary }) => {
    const [logsData, setLogsData] = useState({ count: 0, logs: [] });
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);

    const fetchLogs = async () => {
        if (!apiaryId) return;
        setLoading(true);
        try {
            const data = await getColonyLogs(apiaryId);
            setLogsData(data || { count: 0, logs: [] });
            if (refreshApiary) refreshApiary();
        } catch (error) {
            console.error('Failed to load colony logs:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchLogs();
    }, [apiaryId]);

    const summaryStats = useMemo(() => {
        const logs = logsData.logs || [];
        const additions = logs.filter(l => l.type === 'ADD').reduce((acc, l) => acc + (l.count || 0), 0);
        const subtractions = logs.filter(l => l.type === 'SUBTRACT').reduce((acc, l) => acc + (l.count || 0), 0);
        const net = additions - subtractions;
        
        // Latest total calculation
        const currentTotal = logs.length > 0 ? logs[0].total_at_time : 'N/A';
        
        return {
            additions,
            subtractions,
            net: net > 0 ? `+${net}` : net,
            currentTotal,
            count: logs.length
        };
    }, [logsData]);

    const formatReason = (reason) => {
        if (!reason) return '-';
        return reason.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ');
    };

    const columns = [
        {
            dataField: 'logged_on',
            text: 'Date',
            sort: true,
            formatter: (cell) => moment(cell).format('DD MMM YYYY'),
            style: { fontWeight: 'bold' }
        },
        {
            dataField: 'type',
            text: 'Action',
            sort: true,
            formatter: (cell) => <TypeBadge type={cell}>{cell}</TypeBadge>,
            headerStyle: { textAlign: 'center' },
            style: { textAlign: 'center' }
        },
        {
            dataField: 'source_or_reason',
            text: 'Source / Reason',
            formatter: (cell) => formatReason(cell),
        },
        {
            dataField: 'count',
            text: 'Qty',
            sort: true,
            formatter: (cell, row) => (
                <span style={{ color: row.type === 'ADD' ? '#00b894' : '#d63031' }}>
                    {row.type === 'ADD' ? '+' : '-'}{cell}
                </span>
            ),
            headerStyle: { textAlign: 'center' },
            style: { textAlign: 'center', fontWeight: 700 }
        },
        {
            dataField: 'total_before',
            text: 'Initial',
            headerStyle: { textAlign: 'center' },
            style: { textAlign: 'center', color: '#b2bec3' }
        },
        {
            dataField: 'total_at_time',
            text: 'Result',
            headerStyle: { textAlign: 'center' },
            style: { textAlign: 'center', fontWeight: 700, color: '#2d3436' }
        },
        {
            dataField: 'logged_by_name',
            text: 'Logged By',
            formatter: (cell) => <span style={{ fontSize: '13px', color: '#636e72' }}>{cell || 'System'}</span>
        }
    ];

    return (
        <MainContainer>
            <HeaderSection>
                <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                    <h4 style={{ margin: 0, color: '#2d3436', fontWeight: 700 }}>Strength History</h4>
                    <Tag color="green" style={{ borderRadius: '12px', fontWeight: 700 }}>{summaryStats.count} Total Entries</Tag>
                </div>
                <Button 
                    variant="primary" 
                    onClick={() => setShowForm(true)}
                    style={{ 
                        background: '#0984e3', 
                        borderColor: '#0984e3', 
                        borderRadius: '8px', 
                        fontWeight: 700,
                        padding: '8px 20px',
                        boxShadow: '0 4px 10px rgba(9, 132, 227, 0.2)'
                    }}
                >
                    <i className="fa fa-plus-circle mr-2"></i> Log Adjustment
                </Button>
            </HeaderSection>

            <SummaryCards>
                <StatCard color="#f39c12" color2="#ffcc00">
                    <div className="label">Current Total Hives</div>
                    <div className="value">{summaryStats.currentTotal} <span style={{ fontSize: '16px' }}>Units</span></div>
                    <div className="sub-value">Current authoritative count</div>
                </StatCard>
                <StatCard color="#00b894" color2="#55efc4">
                    <div className="label">Total Additions</div>
                    <div className="value">{summaryStats.additions} <span style={{ fontSize: '16px' }}>Colonies</span></div>
                    <div className="sub-value">Manual logs only</div>
                </StatCard>
                <StatCard color="#d63031" color2="#ff7675">
                    <div className="label">Total Mortality</div>
                    <div className="value">{summaryStats.subtractions} <span style={{ fontSize: '16px' }}>Colonies</span></div>
                    <div className="sub-value">Manual logs only</div>
                </StatCard>
                <StatCard color="#0984e3" color2="#74b9ff">
                    <div className="label">Net Adjustment</div>
                    <div className="value">{summaryStats.net} <span style={{ fontSize: '16px' }}>Colonies</span></div>
                    <div className="sub-value">Current hive_adjustment field</div>
                </StatCard>
            </SummaryCards>

            <LoadingOverlay
                active={loading}
                spinner={<BounceLoader color={'#0984e3'} />}
                text='Fetching colony logs...'
                styles={{
                    overlay: (base) => ({
                        ...base,
                        background: 'rgba(255, 255, 255, 0.8)',
                        zIndex: 10
                    })
                }}
            >
                {logsData.logs.length === 0 && !loading ? (
                    <Empty 
                        image={Empty.PRESENTED_IMAGE_SIMPLE} 
                        description="No colony logs found" 
                        style={{ padding: '60px 0', background: '#fff', borderRadius: '12px', border: '1px dashed #eee' }}
                    />
                ) : (
                    <TableWrapper>
                        <BootstrapTable
                            keyField="logged_on"
                            data={logsData.logs}
                            columns={columns}
                            hover
                            bordered={false}
                            className="modern-table"
                            pagination={paginationFactory({
                                sizePerPage: 10,
                                hideSizePerPage: true,
                                hidePageListOnlyOnePage: true
                            })}
                        />
                    </TableWrapper>
                )}
            </LoadingOverlay>

            <ColonyLogFormModal 
                show={showForm} 
                onHide={() => setShowForm(false)} 
                apiaryId={apiaryId}
                onSuccess={fetchLogs}
            />
        </MainContainer>
    );
};

export default NectarApiaryStrength;
